import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, RefreshCw } from "lucide-react";
import MainLayout from "@/components/layouts/MainLayout";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import UsersTable from "@/components/users/UsersTable";
import CreateUserForm from "@/components/users/CreateUserForm";
import CommissionTypesManager from "@/components/commission/CommissionTypesManager";
import { UserWithProfile } from "@/models/user";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

const Users = () => {
  const { isAdmin } = useAuth();
  const [users, setUsers] = useState<UserWithProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log("Obteniendo usuarios...");
      
      // Obtener perfiles de la tabla profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .order('email', { ascending: true });
        
      if (profilesError) {
        console.error("Error al obtener perfiles:", profilesError);
        throw profilesError;
      }

      console.log("Perfiles obtenidos:", profiles?.length || 0);

      if (!profiles || profiles.length === 0) {
        setUsers([]);
        return;
      }

      // Transformar perfiles al formato UserWithProfile
      const usersWithProfiles: UserWithProfile[] = profiles.map(profile => ({
        id: profile.id,
        email: profile.email || '',
        created_at: '',
        app_metadata: {},
        user_metadata: {},
        aud: 'authenticated',
        confirmation_sent_at: null,
        confirmed_at: null,
        recovery_sent_at: null,
        role: '',
        updated_at: '',
        last_sign_in_at: null,
        identities: [],
        factors: [],
        profile: {
          id: profile.id,
          full_name: profile.full_name,
          email: profile.email,
          role: profile.role as "admin" | "seller" | "contabilidad",
          commission_percentage: profile.commission_percentage
        }
      }));

      console.log("Usuarios transformados:", usersWithProfiles.length);
      setUsers(usersWithProfiles);
      
    } catch (err) {
      console.error("Error al cargar usuarios:", err);
      const errorMessage = err instanceof Error ? err.message : 'Error desconocido';
      setError(`Error al cargar usuarios: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  // Función para manejar cambios de usuarios (crear, actualizar, eliminar)
  const handleUserChange = async () => {
    console.log("Cambio de usuario detectado, recargando lista...");
    try {
      await fetchUsers();
      console.log("Lista de usuarios actualizada exitosamente");
    } catch (error) {
      console.error("Error al actualizar lista de usuarios:", error);
      toast.error("Error al actualizar la lista de usuarios");
    }
  };

  useEffect(() => {
    if (isAdmin) {
      console.log("Admin detectado, obteniendo usuarios iniciales");
      fetchUsers();
    }
  }, [isAdmin]);

  // Redirigir si no es administrador
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <MainLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Empleados y comisiones</h1>
          <p className="text-muted-foreground">
            Administre los empleados del sistema, sus roles y comisiones.
          </p>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Crear Usuario</CardTitle>
                <CardDescription>
                  Añade un nuevo usuario al sistema
                </CardDescription>
              </CardHeader>
              <CardContent>
                <CreateUserForm onUserCreated={handleUserChange} />
              </CardContent>
            </Card>
          </div>
          
          <div className="md:col-span-2">
            <UsersTable 
              users={users} 
              isLoading={loading} 
              onUserUpdated={handleUserChange} 
              onUserDeleted={handleUserChange}
            />
          </div>
        </div>

        {/* Sección de Configuración de Comisiones */}
        <div className="mt-8">
          <CommissionTypesManager />
        </div>
      </div>
    </MainLayout>
  );
};

export default Users;
